import { upload } from '@/lib/coreStorage';

// ─────────────────────────────────────────────────────────────
// Pièces jointes des messages privés (photo / document).
// L'upload passe par l'abstraction de stockage du Core (SeaweedFS / Backend)
// et renvoie une URL publique directement affichable.
// ─────────────────────────────────────────────────────────────

export type AttachmentType = 'image' | 'document';

export interface MessageAttachment {
  url: string;
  name: string;
  type: AttachmentType;
}

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic)$/i;

/** Détermine si la pièce jointe est une image ou un document. */
export function attachmentKind(mime: string, name: string): AttachmentType {
  if (mime && mime.startsWith('image/')) return 'image';
  return IMAGE_EXT_RE.test(name) ? 'image' : 'document';
}

/** Taille maximale autorisée pour une pièce jointe (10 Mo). */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * Upload une pièce jointe dans le dossier du propriétaire (marchand ou client).
 * Renvoie { url, name, type } prêt à stocker dans la table `messages`.
 */
export async function uploadMessageAttachment(
  file: File,
  ownerId: number | string,
): Promise<MessageAttachment> {
  const type = attachmentKind(file.type, file.name);
  const obj = await upload(file, `messages/${ownerId}`, {
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
  });
  return { url: obj.url, name: file.name, type };
}

/** Icône Remix associée au type de pièce jointe. */
export function attachmentIcon(type: string | null | undefined, name?: string | null): string {
  if (type === 'image' || (!type && name && IMAGE_EXT_RE.test(name))) return 'ri-image-line';
  if (name && /\.(pdf)$/i.test(name)) return 'ri-file-pdf-2-line';
  if (name && /\.(docx?|odt|rtf)$/i.test(name)) return 'ri-file-word-2-line';
  if (name && /\.(xlsx?|csv|ods)$/i.test(name)) return 'ri-file-excel-2-line';
  return 'ri-file-text-line';
}

/** Vrai si la pièce jointe doit être affichée comme une image. */
export function isImageAttachment(type: string | null | undefined, name?: string | null): boolean {
  if (type === 'image') return true;
  if (!type && name) return IMAGE_EXT_RE.test(name);
  return false;
}